import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { CgMenuLeft } from "react-icons/cg";
import { RiDeleteBinLine, RiEditLine } from "react-icons/ri";
import Breadcrumbs from "../../components/Breadcrumbs";
import NewTableComponent from "../../components/NewTableComponent";
import Stats from "../../components/Stats";
import TablePopover from "../../components/TablePopover";
import ConfirmDeleteModal from "../../components/modals/ConfirmDeleteModal";
import FuelRequestModal from "../../components/modals/FuelRequestModal";
import NoPermissionModal from "../../components/modals/NoPermissionModal";
import { apiService } from "../../services/apiService";
import {
  dateMonthYear,
  hasFuelRequestPermission,
  hasPermission,
  hasTripPermission,
  isSuperAdminRole,
} from "../../utils/helper";
import { AppMainModules, TripPermission, TripSubModules } from "../../utils/app-constant";
import {
  activeStatsIcon,
  lateStatsIcon,
  statsCompletedIcon,
  tripStatsIcon,
} from "../../utils/svgs";
import { errorNotify, successNotify } from "../../utils/toast";
import "../../styles/Trip.css";
import "../../styles/Access.css";
import "../../styles/TripsList.css";

const renderStatusBadge = ({
  statusValue,
  canChangeStatus = false,
  isOpen = false,
  isUpdating = false,
  className = "",
}) => {
  const rawStatus = String(statusValue || "")
    .trim()
    .toLowerCase();

  const statusConfig = {
    pending: { label: "Pending", className: "status_pending" },
    approved: { label: "Approved", className: "status_approved" },
    rejected: { label: "Rejected", className: "status_rejected" },
    cancelled: { label: "Cancelled", className: "status_default" },
  };

  const config = statusConfig[rawStatus] || {
    label: rawStatus
      ? rawStatus.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
      : "—",
    className: "status_default",
  };

  return (
    <span
      className={`petty_cash_status_badge ${config.className} ${canChangeStatus ? "is_interactive" : ""
        } ${isOpen ? "is_open" : ""} ${className}`}
    >
      {isUpdating ? (
        <span className="petty_cash_badge_spinner" aria-hidden="true" />
      ) : (
        <span className="petty_cash_badge_dot" aria-hidden="true" />
      )}
      <span className="petty_cash_badge_label">
        {isUpdating ? "Updating..." : config.label}
      </span>
      {canChangeStatus && !isUpdating && (
        <span className="petty_cash_badge_chevron" aria-hidden="true">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path
              d="M2.5 3.75L5 6.25L7.5 3.75"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      )}
    </span>
  );
};

const FuelRequests = () => {
  const outletContext = useOutletContext() || {};
  const toggleMobileSidebar = outletContext?.toggleMobileSidebar;

  const canEditFuelRequest = useMemo(
    () => isSuperAdminRole() || hasFuelRequestPermission(TripPermission.FuelRequestEdit),
    []
  );
  const canDeleteFuelRequest = useMemo(
    () => isSuperAdminRole() || hasFuelRequestPermission(TripPermission.FuelRequestDelete),
    []
  );
  const canUpdateFuelRequestStatus = useMemo(
    () =>
      isSuperAdminRole() ||
      hasFuelRequestPermission(TripPermission.FuelRequestStatusUpdate) ||
      hasFuelRequestPermission("fuel-request-approval-status"),
    [],
  );

  const [fuelRequests, setFuelRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [searchFilters, setSearchFilters] = useState({});

  const [statusPopup, setStatusPopup] = useState({
    open: false,
    rowId: null,
    anchorPos: { x: 0, y: 0 },
  });
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [noPermissionModalOpen, setNoPermissionModalOpen] = useState(false);
  const [restrictedStatusInfo, setRestrictedStatusInfo] = useState(null);
  const statusPopupRef = useRef();

  const [deletePopup, setDeletePopup] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState(null);

  const fetchFuelRequests = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.getFuelRequestsPaginated({
        page: currentPage,
        limit: limit,
        search_text: searchText,
        searchFilters: searchFilters,
      });

      const list = response?.data?.results || response?.data?.data || [];
      const meta = response?.data?.meta || {};
      const total = meta?.totalItems ?? response?.data?.total ?? list.length;
      const pages = meta?.totalPages ?? Math.max(1, Math.ceil(total / limit));

      setFuelRequests(list);
      setTotalItems(total);
      setTotalPages(pages);
    } catch (err) {
      errorNotify(err || "Failed to fetch fuel requests");
    } finally {
      setLoading(false);
    }
  }, [currentPage, limit, searchText, searchFilters]);

  useEffect(() => {
    fetchFuelRequests();
  }, [fetchFuelRequests]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        event.target &&
        typeof event.target.closest === "function" &&
        event.target.closest(".petty_cash_status_trigger")
      ) {
        return;
      }

      if (
        statusPopupRef.current &&
        !statusPopupRef.current.contains(event.target)
      ) {
        setStatusPopup({ open: false, rowId: null, anchorPos: { x: 0, y: 0 } });
      }
    }

    function handleScrollOrResize() {
      setStatusPopup((prev) => {
        if (!prev.open) return prev;
        return { open: false, rowId: null, anchorPos: { x: 0, y: 0 } };
      });
    }

    if (statusPopup.open) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside, { passive: true });
      window.addEventListener("scroll", handleScrollOrResize, true);
      window.addEventListener("resize", handleScrollOrResize);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [statusPopup.open]);

  const handleStatusClick = useCallback(
    (event, row) => {
      event.stopPropagation();
      const normalizedStatus = String(row?.status || "").toLowerCase().trim();
      if (statusUpdatingId === row?.id) return;

      if (!canUpdateFuelRequestStatus) {
        setStatusPopup({ open: false, rowId: null, anchorPos: { x: 0, y: 0 } });
        setRestrictedStatusInfo({
          displayId:
            row?.trip?.trip_id ||
            (row?.trip_id ? `TR-${row.trip_id}` : `#${row?.id}`),
          currentStatus: row?.status || "Pending",
        });
        setNoPermissionModalOpen(true);
        return;
      }

      if (normalizedStatus !== "pending") return;

      const rect = event.currentTarget.getBoundingClientRect();
      setStatusPopup((prev) => {
        if (prev.open && prev.rowId === row.id) {
          return { open: false, rowId: null, anchorPos: { x: 0, y: 0 } };
        }
        return {
          open: true,
          rowId: row.id,
          anchorPos: {
            x: rect.right,
            y: rect.top,
            left: rect.left,
            right: rect.right,
            top: rect.top,
            bottom: rect.bottom,
          },
        };
      });
    },
    [canUpdateFuelRequestStatus, statusUpdatingId]
  );

  const handleStatusSelect = useCallback(
    async (status, row) => {
      if (!row?.id || statusUpdatingId) return;

      if (!canUpdateFuelRequestStatus) {
        setStatusPopup({ open: false, rowId: null, anchorPos: { x: 0, y: 0 } });
        setRestrictedStatusInfo({
          displayId:
            row?.trip?.trip_id ||
            (row?.trip_id ? `TR-${row.trip_id}` : `#${row?.id}`),
          currentStatus: row?.status || "Pending",
        });
        setNoPermissionModalOpen(true);
        return;
      }

      setStatusPopup({ open: false, rowId: null, anchorPos: { x: 0, y: 0 } });
      setStatusUpdatingId(row.id);
      try {
        await apiService.updateFuelRequestStatus({
          fuel_request_id: row.id,
          status,
        });
        successNotify("Status updated successfully");
        fetchFuelRequests();
      } catch (err) {
        errorNotify(err || "Failed to update fuel request status");
      } finally {
        setStatusUpdatingId(null);
      }
    },
    [canUpdateFuelRequestStatus, fetchFuelRequests, statusUpdatingId]
  );

  const StatusPopup = useCallback(
    ({ row }) => {
      const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;
      return (
        <TablePopover
          innerRef={statusPopupRef}
          anchorRect={statusPopup.anchorPos}
          popupHeight={96}
          popupWidth={144}
          align={isMobile ? "right" : "left"}
          offset={6}
          style={{
            padding: 0,
            background: "transparent",
            border: "none",
            boxShadow: "none",
          }}
        >
          <div className="petty_cash_status_popover_box">
            <div className="petty_cash_status_menu">
              <div className="petty_cash_status_menu_title">Update Status</div>
              <button
                type="button"
                className="petty_cash_status_option petty_cash_status_option_approved"
                onClick={(e) => {
                  e.stopPropagation();
                  handleStatusSelect("approved", row);
                }}
              >
                <span className="petty_cash_status_option_icon">
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                    <path
                      d="M1.25 4L3.75 6.5L8.75 1.5"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span>Approved</span>
              </button>
              <button
                type="button"
                className="petty_cash_status_option petty_cash_status_option_rejected"
                onClick={(e) => {
                  e.stopPropagation();
                  handleStatusSelect("rejected", row);
                }}
              >
                <span className="petty_cash_status_option_icon">
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                    <path
                      d="M1.5 1.5L6.5 6.5M6.5 1.5L1.5 6.5"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span>Rejected</span>
              </button>
            </div>
          </div>
        </TablePopover>
      );
    },
    [handleStatusSelect, statusPopup.anchorPos]
  );

  const handleOpenEdit = (id) => {
    if (!id || !canEditFuelRequest) return;
    setSelectedRequestId(id);
    setEditModalOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteId || !canDeleteFuelRequest) return;
    setDeleteLoading(true);
    try {
      await apiService.deleteFuelRequest(deleteId);
      successNotify("Fuel request deleted successfully");
      setDeletePopup(false);
      setDeleteId(null);
      if (fuelRequests.length === 1 && currentPage > 1) {
        setCurrentPage((prev) => Math.max(1, prev - 1));
      } else {
        fetchFuelRequests();
      }
    } catch (error) {
      errorNotify(error || "Failed to delete fuel request");
    } finally {
      setDeleteLoading(false);
    }
  };

  const statsData = useMemo(() => {
    const totalCount = totalItems || fuelRequests.length;
    const pendingCount = fuelRequests.filter((r) => String(r?.status).toLowerCase() === "pending").length;
    const approvedCount = fuelRequests.filter((r) => String(r?.status).toLowerCase() === "approved").length;
    const totalRequestedLtrs = fuelRequests.reduce((acc, r) => acc + (Number(r?.requested_fuel_quantity) || 0), 0);

    return [
      {
        title: "Total Requests",
        value: String(totalCount),
        text: <span>Total Fuel Requests</span>,
        icon: tripStatsIcon,
      },
      {
        title: "Pending",
        value: String(pendingCount),
        text: <span>Awaiting Approval</span>,
        icon: lateStatsIcon,
        className: "late",
      },
      {
        title: "Approved",
        value: String(approvedCount),
        text: <span>Approved Requests</span>,
        icon: statsCompletedIcon,
      },
      {
        title: "Total Requested Fuel",
        value: `${totalRequestedLtrs.toFixed(1)} L`,
        text: <span>Across Current Page</span>,
        icon: activeStatsIcon,
      },
    ];
  }, [fuelRequests, totalItems]);

  const columns = useMemo(
    () => [
      {
        name: "Request ID",
        selector: (row) => row?.id,
        cell: (row) =>
          canEditFuelRequest ? (
            <span
              className="hyperlink"
              role="button"
              tabIndex={0}
              onClick={() => handleOpenEdit(row?.id)}
              title="Click to view & edit details"
            >
              {row?.request_id || row?.id}
            </span>
          ) : (
            <span>{row?.request_id || row?.id}</span>
          ),
        minWidth: "150px",
      },
      {
        name: "Trip ID",
        selector: (row) => row?.trip?.trip_id || row?.trip_id || "-",
        cell: (row) => {
          const tripCode = row?.trip?.trip_id || (row?.trip_id ? `TR-${row.trip_id}` : "-");
          return (
            <span
              style={{
                backgroundColor: "#F3E8FF",
                color: "#7E22CE",
                padding: "3px 8px",
                borderRadius: "6px",
                fontWeight: "600",
                fontSize: "12px",
              }}
            >
              {tripCode}
            </span>
          );
        },
        minWidth: "180px",
        tableColumn: true,
        filterName: "tripFilter",
        type: "text",
      },
      {
        name: "Vehicle",
        selector: (row) => row?.vehicle?.registration_number || row?.vehicle_id || "-",
        cell: (row) => {
          const reg = row?.vehicle?.registration_number?.toUpperCase();
          const fuelType = row?.vehicle?.fuel_type || row?.fuel_type;
          return (
            <div>
              <span className="fw-semibold">{reg || (row?.vehicle_id ? `#${row.vehicle_id}` : "-")}</span>
              {fuelType && (
                <span className="d-block text-muted" style={{ fontSize: "11px" }}>
                  {fuelType.toUpperCase()}
                </span>
              )}
            </div>
          );
        },
        minWidth: "180px",
        tableColumn: true,
        filterName: "vehicleFilter",
        type: "text",
      },
      {
        name: "Status",
        selector: (row) => row?.status,
        cell: (row) => {
          const normalizedStatus = String(row?.status || "").toLowerCase().trim();
          const isPending = normalizedStatus === "pending";
          const isOpen = statusPopup.open && statusPopup.rowId === row.id;
          const isUpdating = statusUpdatingId === row?.id;
          return (
            <div className="petty_cash_status_trigger_wrapper">
              <span
                className={`petty_cash_status_trigger ${isPending || !canUpdateFuelRequestStatus ? "is_clickable" : ""}`}
                onClick={(event) => handleStatusClick(event, row)}
              >
                {renderStatusBadge({
                  statusValue: row?.status,
                  canChangeStatus: isPending && canUpdateFuelRequestStatus,
                  isOpen,
                  isUpdating,
                })}
              </span>
              {isOpen && <StatusPopup row={row} />}
            </div>
          );
        },
        minWidth: "160px",
        tableColumn: true,
        filterName: "statusFilter",
        type: "select",
        options: [
          { label: "Pending", value: "pending" },
          { label: "Approved", value: "approved" },
          { label: "Rejected", value: "rejected" },
          { label: "Cancelled", value: "cancelled" },
        ],
      },
      {
        name: "Driver",
        selector: (row) => row?.driver?.name || row?.driver?.username || row?.trip?.primary_driver_contact || "-",
        cell: (row) => {
          const driverName = row?.driver?.name || row?.driver?.username;
          const contact = row?.trip?.primary_driver_contact;
          return (
            <div>
              <span>{driverName || "-"}</span>
              {contact && (
                <span className="d-block text-muted" style={{ fontSize: "11px" }}>
                  {contact}
                </span>
              )}
            </div>
          );
        },
        minWidth: "180px",
        tableColumn: true,
        filterName: "driverFilter",
        type: "text",
      },
      {
        name: "Requested By",
        selector: (row) => row?.requester?.username || row?.requester?.email || "-",
        cell: (row) => {
          const requester = row?.requester;
          const name = requester?.username || (row?.requested_by ? `User #${row.requested_by}` : "-");
          const email = requester?.email;
          return (
            <div>
              <span className="fw-medium">{name}</span>
              {email && (
                <span className="d-block text-muted" style={{ fontSize: "11px" }}>
                  {email}
                </span>
              )}
            </div>
          );
        },
        minWidth: "200px",
        tableColumn: true,
        filterName: "requestedByFilter",
        type: "text",
      },
      {
        name: "Fuel Type",
        selector: (row) => row?.fuel_type || row?.vehicle?.fuel_type || "-",
        cell: (row) => {
          const fType = row?.fuel_type || row?.vehicle?.fuel_type;
          return fType ? (
            <span style={{ textTransform: "capitalize", fontWeight: "500" }}>
              {fType}
            </span>
          ) : "—";
        },
        minWidth: "160px",
        tableColumn: true,
        filterName: "fuelTypeFilter",
        type: "select",
        options: [
          { label: "Diesel", value: "Diesel" },
          { label: "Petrol", value: "petrol" },
          { label: "CNG", value: "CNG" },
        ],
      },
      {
        name: "Requested Fuel",
        selector: (row) => row?.requested_fuel_quantity,
        cell: (row) => (
          <span className="fw-bold" style={{ color: "#4361ee" }}>
            {row?.requested_fuel_quantity
              ? `${Number(row.requested_fuel_quantity).toLocaleString(undefined, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 2,
              })} ltrs`
              : "0 ltrs"}
          </span>
        ),
        minWidth: "180px",
      },
      {
        name: "Approved Fuel",
        selector: (row) => row?.approved_fuel_quantity,
        cell: (row) => (
          <span>
            {row?.approved_fuel_quantity && Number(row.approved_fuel_quantity) > 0
              ? `${Number(row.approved_fuel_quantity).toLocaleString(undefined, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 2,
              })} ltrs`
              : "—"}
          </span>
        ),
        minWidth: "170px",
      },
      {
        name: "Requested Date",
        selector: (row) => row?.created_at,
        cell: (row) => (row?.created_at ? dateMonthYear(row.created_at) : "-"),
        minWidth: "180px",
        tableColumn: true,
        filterName: "dateRangeFilter",
        type: "date",
      },
      {
        name: "Remarks",
        selector: (row) => row?.remarks || "-",
        cell: (row) => row?.remarks || "—",
        minWidth: "220px",
      },
      {
        name: "Action",
        cell: (row) => (
          <div className="d-flex align-items-center gap-2">
            {canEditFuelRequest && (
              <button
                type="button"
                className="table_action_delete_btn"
                style={{ color: "#4361ee" }}
                title="Edit Fuel Request"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenEdit(row?.id);
                }}
              >
                <RiEditLine style={{ fontSize: "16px", color: "#4361ee" }} />
              </button>
            )}
            {canDeleteFuelRequest && (
              <button
                type="button"
                className="table_action_delete_btn"
                title="Delete Fuel Request"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteId(row?.id);
                  setDeletePopup(true);
                }}
              >
                <RiDeleteBinLine style={{ fontSize: "16px", color: "#ef4444" }} />
              </button>
            )}
          </div>
        ),
        minWidth: "100px",
        grow: 0,
      },
    ],
    [
      canEditFuelRequest,
      canDeleteFuelRequest,
      canUpdateFuelRequestStatus,
      statusPopup,
      statusUpdatingId,
      handleStatusClick,
    ]
  );

  return (
    <div className="trip_page fuel_requests_page">

      {/* ── Mobile Header ── */}
      <div className="sub_page_mobile_header">
        <div className="sub_page_mobile_header_left">
          <button className="trips_mobile_menu_btn" onClick={toggleMobileSidebar}>
            <CgMenuLeft size={22} color="#1c1c1c" />
          </button>
          <h2 className="sub_page_mobile_title">Fuel Requests</h2>
        </div>
      </div>

      <div className="desktop_only_el">
        <Breadcrumbs values={["Fuel Management", "Fuel Requests"]} />
      </div>

      <ConfirmDeleteModal
        show={deletePopup}
        onClose={() => {
          if (!deleteLoading) {
            setDeletePopup(false);
            setDeleteId(null);
          }
        }}
        onConfirm={handleDelete}
        loading={deleteLoading}
        title="Are you sure you want to delete this fuel request?"
        description="This action cannot be undone. The fuel request will be permanently removed."
      />

      <FuelRequestModal
        show={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedRequestId(null);
        }}
        requestId={selectedRequestId}
        onSuccess={() => {
          fetchFuelRequests();
        }}
      />

      {/* Access Denied / Permission Required Modal */}
      <NoPermissionModal
        show={noPermissionModalOpen}
        onClose={() => setNoPermissionModalOpen(false)}
        title="Permission Required"
        message="You do not have permission to update the status of this fuel request. Please contact your system administrator to request access."
        actionName="Status Update"
        permissionName="fuel-request-approval-status"
        tripId={restrictedStatusInfo?.displayId}
      />

      <div className="access_view sub_page_desktop_header">
        <h2>Fuel Requests</h2>
      </div>

      <Stats statsData={statsData} />

      {/* ── Desktop Table ── */}
      <div className="desktop_table_view">
        <NewTableComponent
          showAddBtn={false}
          columns={columns}
          data={fuelRequests}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          limit={limit}
          setLimit={setLimit}
          totalItems={totalItems}
          totalPages={totalPages}
          onSearch={setSearchText}
          searchValue={searchText}
          loading={loading}
          paginationLoading={loading}
          setSearchFilters={setSearchFilters}
          searchFilters={searchFilters}
          tableClassName="fuel_requests_table"
        />
      </div>

      {/* ── Mobile Cards ── */}
      <div className="mobile_cards_view">
        <div className="mobile_search_bar">
          <input
            type="text"
            className="mobile_search_input"
            placeholder="Search fuel requests..."
            value={searchText}
            onChange={(e) => { setSearchText(e.target.value); setCurrentPage(1); }}
          />
        </div>

        {loading ? (
          <div className="mobile_cards_loading">Loading…</div>
        ) : fuelRequests.length === 0 ? (
          <div className="mobile_no_data">No fuel requests found</div>
        ) : (
          fuelRequests.map((row) => (
            <div key={row?.id} className="mobile_data_card">
              {/* Header: Request ID + Status */}
              <div className="mobile_card_header">
                <div className="mobile_card_id">
                  {canEditFuelRequest ? (
                    <span className="hyperlink" role="button" onClick={() => handleOpenEdit(row?.id)}>
                      {row?.request_id || row?.id}
                    </span>
                  ) : (
                    <span>{row?.request_id || row?.id}</span>
                  )}
                </div>
                {(() => {
                  const normalizedStatus = String(row?.status || "").toLowerCase().trim();
                  const isPending = normalizedStatus === "pending";
                  const isOpen = statusPopup.open && statusPopup.rowId === row.id;
                  const isUpdating = statusUpdatingId === row?.id;
                  return (
                    <div className="petty_cash_status_trigger_wrapper">
                      <span
                        className={`petty_cash_status_trigger ${isPending || !canUpdateFuelRequestStatus ? "is_clickable" : ""}`}
                        onClick={(event) => handleStatusClick(event, row)}
                      >
                        {renderStatusBadge({
                          statusValue: row?.status,
                          canChangeStatus: isPending && canUpdateFuelRequestStatus,
                          isOpen,
                          isUpdating,
                        })}
                      </span>
                      {isOpen && <StatusPopup row={row} />}
                    </div>
                  );
                })()}
              </div>

              {/* Trip ID + Vehicle */}
              <div className="mobile_card_row">
                <div className="mobile_card_field">
                  <span className="mobile_card_label">Trip ID</span>
                  <span className="mobile_card_trip_badge">
                    {row?.trip?.trip_id || (row?.trip_id ? `TR-${row.trip_id}` : "—")}
                  </span>
                </div>
                <div className="mobile_card_field">
                  <span className="mobile_card_label">Vehicle</span>
                  <span className="mobile_card_value">
                    {row?.vehicle?.registration_number?.toUpperCase() || (row?.vehicle_id ? `#${row.vehicle_id}` : "—")}
                  </span>
                </div>
              </div>

              {/* Driver + Requested By */}
              <div className="mobile_card_row">
                <div className="mobile_card_field">
                  <span className="mobile_card_label">Driver</span>
                  <span className="mobile_card_value">{row?.driver?.name || row?.driver?.username || "—"}</span>
                </div>
                <div className="mobile_card_field">
                  <span className="mobile_card_label">Requested By</span>
                  <span className="mobile_card_value">
                    {row?.requester?.username || (row?.requested_by ? `User #${row.requested_by}` : "—")}
                  </span>
                </div>
              </div>

              {/* Requested + Approved Fuel */}
              <div className="mobile_card_row">
                <div className="mobile_card_field">
                  <span className="mobile_card_label">Requested</span>
                  <span className="mobile_card_amount">
                    {row?.requested_fuel_quantity
                      ? `${Number(row.requested_fuel_quantity).toFixed(1)} L`
                      : "0 L"}
                  </span>
                </div>
                <div className="mobile_card_field">
                  <span className="mobile_card_label">Approved</span>
                  <span className="mobile_card_value">
                    {row?.approved_fuel_quantity && Number(row.approved_fuel_quantity) > 0
                      ? `${Number(row.approved_fuel_quantity).toFixed(1)} L`
                      : "—"}
                  </span>
                </div>
              </div>

              {/* Fuel Type + Date */}
              <div className="mobile_card_row">
                <div className="mobile_card_field">
                  <span className="mobile_card_label">Fuel Type</span>
                  <span className="mobile_card_value" style={{ textTransform: "capitalize" }}>
                    {row?.fuel_type || row?.vehicle?.fuel_type || "—"}
                  </span>
                </div>
                <div className="mobile_card_field">
                  <span className="mobile_card_label">Date</span>
                  <span className="mobile_card_value">{row?.created_at ? dateMonthYear(row.created_at) : "—"}</span>
                </div>
              </div>

              {/* Remarks */}
              {row?.remarks && (
                <div className="mobile_card_remarks">
                  <span className="mobile_card_label">Remarks</span>
                  <div style={{ marginTop: "2px" }}>{row.remarks}</div>
                </div>
              )}

              {/* Actions */}
              {(canEditFuelRequest || canDeleteFuelRequest) && (
                <div className="mobile_card_actions">
                  {canEditFuelRequest && (
                    <button
                      type="button"
                      className="mobile_card_action_btn mobile_card_action_edit"
                      onClick={() => handleOpenEdit(row?.id)}
                    >
                      <RiEditLine /> Edit
                    </button>
                  )}
                  {canDeleteFuelRequest && (
                    <button
                      type="button"
                      className="mobile_card_action_btn mobile_card_action_delete"
                      onClick={() => { setDeleteId(row?.id); setDeletePopup(true); }}
                    >
                      <RiDeleteBinLine /> Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          ))
        )}

        {/* Mobile Pagination */}
        {totalPages > 1 && (
          <div className="mobile_pagination">
            <button
              className="mobile_page_btn"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >← Prev</button>
            <span className="mobile_page_info">Page {currentPage} of {totalPages}</span>
            <button
              className="mobile_page_btn"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            >Next →</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FuelRequests;
